// engine-exception: Home composes ONE permission-aware aggregate read (GET /v1/home) across every module into stat tiles, a "Today" strip and short lists — no single resource backs it, so no ScreenDef kind renders it
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../../engine/api/hooks';
import { Button } from '../../engine/design/primitives/Button';
import { EmptyState, ErrorBanner, Skeleton } from '../../engine/design/primitives/Misc';
import { useTiles } from '../../engine/api/context';
import { Icon, type IconName } from '../../engine/design/icons';
import { formatDate, formatDateTime, formatRelativeDays } from '../../lib/format';
import { GetStarted } from '../../components/GetStarted';
import { NovaHub } from './NovaHub';
import { useDataSource } from '../../engine/meta/useDataSource';
import { Freshness, listLink } from './HomeBands';
import { BANDS } from './tiles';
import { arrange, CustomiseHome, useHomeLayout } from './layout';
import { ScopeBar, useHomeScope } from './scope';
import type { HomeTiles } from './types';

// The Home screen: what needs the caller's attention across the PSA, from one aggregate read
// (docs/integration.md "Home"). Every tile links to the filtered list behind it through the query
// DSL (`?filter[...]`), so the number and the list can never disagree; a tile the API omitted for
// lack of permission is simply not rendered. Money is minor units + currency through <Money>.

export type { HomeTiles } from './types';

const ENTITY_ROUTE: Record<string, string> = { ticket: '/tickets/:id', client: '/clients/:id', contact: '/contacts/:id', site: '/sites/:id', quote: '/quotes/:id', project: '/projects/:id', project_task: '/my-tasks', invoice: '/invoices/:id', opportunity: '/opportunities/:id', contract: '/contracts/:id' };

export { listLink };

function Section({ title, count, to, children }: { title: string; count?: number; to?: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h2 className="hf-section-title">
        <span>
          {title}
          {count != null && <small className="hf-num" style={{ marginLeft: 'var(--s-2)' }}>{count}</small>}
        </span>
        {to && (
          <Link to={to} className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>
            View all →
          </Link>
        )}
      </h2>
      {children}
    </section>
  );
}

function Row({ to, icon, primary, secondary, right }: { to: string; icon: IconName; primary: ReactNode; secondary?: ReactNode; right?: ReactNode }) {
  return (
    <Link to={to} className="hf-row" style={{ textDecoration: 'none', color: 'inherit', padding: 'var(--s-2) 0', borderBottom: '1px solid var(--rule)' }}>
      <Icon name={icon} size={14} className="hf-muted" />
      <span className="hf-grow" style={{ minWidth: 0 }}>
        <span style={{ display: 'block' }}>{primary}</span>
        {secondary && <span className="hf-muted" style={{ display: 'block', fontSize: 'var(--t-12)' }}>{secondary}</span>}
      </span>
      {right && <span className="hf-num hf-muted" style={{ fontSize: 'var(--t-12)', flexShrink: 0 }}>{right}</span>}
    </Link>
  );
}

export function HomeScreen() {
  // One Home for every tenant: the API folds it from NovaPSA's tables, or — for a tenant on its
  // connected system — from that system's tickets, appointments and timesheets (`connected: true`).
  return <NativeHome />;
}

/** Mirrors SOURCE_HOME_MAX_ROWS on the server (src/modules/home/services/sourceHome.ts). */
const SOURCE_HOME_ROWS = 1000;

function NativeHome() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const allTiles = useTiles();
  const { scope, set } = useHomeScope();
  const home = useHomeLayout();
  const [customising, setCustomising] = useState(false);
  const q = useApiQuery<HomeTiles>(['home', scope], '/v1/home', { staleTime: 30_000, query: { technician_id: scope.technician_id, client_id: scope.client_id } });
  const t = q.data;

  const todayItems = useMemo(() => {
    const out: { key: string; to: string; icon: IconName; primary: ReactNode; secondary?: ReactNode; right?: ReactNode }[] = [];
    for (const d of t?.tickets?.due ?? []) out.push({ key: `t:${d.id}`, to: `/tickets/${d.id}`, icon: 'ticket', primary: `${d.ref ?? 'Ticket'} · ${d.summary ?? ''}`, secondary: [d.client_name, d.priority_name, d.status_name].filter(Boolean).join(' · '), right: d.due_at ? (formatRelativeDays(d.due_at) ?? formatDateTime(d.due_at)) : null });
    for (const a of t?.approvals?.items ?? []) out.push({ key: `a:${a.id}`, to: a.ticket_id ? `/tickets/${a.ticket_id}` : '/approvals', icon: 'shield-check', primary: `Approve: ${a.step_name ?? a.process_name}${a.ticket_ref ? ` on ${a.ticket_ref}` : ''}`, secondary: a.ticket_summary ?? a.process_name, right: a.timeout_at ? `by ${formatDate(a.timeout_at)}` : null });
    for (const k of t?.projects?.tasks ?? []) out.push({ key: `k:${k.id}`, to: `/projects/${k.project_id}?tab=tasks&task=${k.id}`, icon: 'check-square', primary: `${k.ref ?? 'Task'} · ${k.title}`, secondary: [k.project_ref, k.project_name].filter(Boolean).join(' '), right: k.due_date ? (formatRelativeDays(k.due_date) ?? formatDate(k.due_date)) : null });
    return out;
  }, [t]);

  const own = !t?.scope?.technician_id || t.scope.technician_id === t.me.technician_id;
  const bands = t
    ? BANDS.map((b) => ({ band: b, items: arrange(allTiles, b.key, home.layout).map((d) => ({ key: d.key, node: d.render(t) })).filter((x) => x.node != null) })).filter((x) => x.items.length > 0)
    : [];

  // Quick-log writes NovaPSA time entries; on a connected system without them (Halo) time goes on the ticket.
  const canLogTime = useDataSource().resourceServed('time-entries');

  return (
    <div className="hf-page">
      <NovaHub
        actions={
          <>
            {canLogTime && (
              <Button icon="timer" onClick={() => navigate('/timesheets?log=1')}>
                Log time
              </Button>
            )}
            <Button variant="primary" icon="ticket" onClick={() => navigate('/tickets?new=1')}>
              New ticket
            </Button>
          </>
        }
      />

      {q.isLoading && <Skeleton lines={6} />}
      {q.error && <ErrorBanner error={q.error} />}

      {t && (
        <>
          {t.finance && <GetStarted stats={t.finance} />}
          <Freshness at={t.generated_at} busy={q.isFetching} onRefresh={() => void qc.invalidateQueries({ queryKey: ['home'] })}>
            <span className="hf-grow" />
            <ScopeBar scope={scope} set={set} me={t.me.technician_id} clientName={t.scope?.client_name ?? null} />
            <Button size="sm" variant="ghost" icon="settings" onClick={() => setCustomising(true)}>
              Customise
            </Button>
          </Freshness>

          {bands.map(({ band, items }) => {
            const grid = (
              <div className="hf-stats" role="list" aria-label={band.key === 'my-week' && !own ? `${t.scope?.technician_name ?? 'Their'} week` : band.label}>
                {items.map((x) => (
                  <div key={x.key} role="listitem">{x.node}</div>
                ))}
              </div>
            );
            return band.title ? (
              <Section key={band.key} title={band.key === 'my-week' && !own ? `${t.scope?.technician_name ?? 'Their'} week` : band.title}>{grid}</Section>
            ) : (
              <div key={band.key}>{grid}</div>
            );
          })}

          <div id="today" />
          <Section title="Today" count={todayItems.length}>
            {todayItems.length === 0 ? (
              <EmptyState title="Nothing due today" hint="Tickets and tasks due today, and approvals waiting for you, land here." icon="sun" />
            ) : (
              <div className="hf-stack" style={{ gap: 0 }}>
                {todayItems.map((i) => (
                  <Row key={i.key} to={i.to} icon={i.icon} primary={i.primary} secondary={i.secondary} right={i.right} />
                ))}
              </div>
            )}
          </Section>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))', gap: 'var(--s-7)' }}>
            {t.quotes && (
              <Section title="Quotes awaiting a decision" count={t.quotes.awaiting} to={listLink('/quotes', [{ field: 'status', op: 'in', value: ['sent', 'viewed'] }])}>
                {t.quotes.items.length === 0 ? (
                  <EmptyState title="No open quotes" icon="quote" />
                ) : (
                  <div className="hf-stack" style={{ gap: 0 }}>
                    {t.quotes.items.map((x) => (
                      <Row key={x.id} to={`/quotes/${x.id}`} icon="quote" primary={`${x.ref} · ${x.title ?? ''}`} secondary={[x.client_name, x.status].filter(Boolean).join(' · ')} right={x.expires_at ? `expires ${formatDate(x.expires_at)}` : null} />
                    ))}
                  </div>
                )}
              </Section>
            )}
            {t.finance && (
              <Section title="Prepay running low" count={t.finance.prepay_low.length} to="/contracts">
                {t.finance.prepay_low.length === 0 ? (
                  <EmptyState title="Prepay balances are healthy" icon="check" />
                ) : (
                  <div className="hf-stack" style={{ gap: 0 }}>
                    {t.finance.prepay_low.map((p) => (
                      <Row key={p.prepay_account_id} to="/contracts" icon="alert-triangle" primary={p.client_name ?? p.contract_ref ?? 'Prepay account'} secondary={p.contract_ref ?? undefined} right={<span>{p.remaining_qty} {p.unit === 'hours' ? 'h' : p.currency} left{p.over_drawn ? ' · overdrawn' : ''}</span>} />
                    ))}
                  </div>
                )}
              </Section>
            )}
            {t.activity && (
              <Section title="Recent activity">
                {t.activity.length === 0 ? (
                  <EmptyState title="No activity yet" icon="activity" />
                ) : (
                  <div className="hf-stack" style={{ gap: 0 }}>
                    {t.activity.map((a) => (
                      <Row key={a.id} to={(ENTITY_ROUTE[a.entity_type] ?? '/').replace(':id', a.entity_id)} icon="activity" primary={a.verb.replace(/[._]/g, ' ')} secondary={a.entity_type.replace(/_/g, ' ')} right={formatDateTime(a.created_at)} />
                    ))}
                  </div>
                )}
              </Section>
            )}
          </div>

          <CustomiseHome
            open={customising}
            onClose={() => setCustomising(false)}
            tiles={allTiles}
            bands={BANDS}
            layout={home.layout}
            canReset={home.personal}
            busy={home.save.isPending || home.reset.isPending}
            onSave={(l) => home.save.mutate(l, { onSuccess: () => setCustomising(false) })}
            onReset={() => home.reset.mutate(undefined, { onSuccess: () => setCustomising(false) })}
          />

          {t.truncated && (
            <p className="hf-muted" style={{ fontSize: 'var(--t-12)', marginTop: 'var(--s-4)' }} role="note">
              Counts cover the first {SOURCE_HOME_ROWS.toLocaleString()} open tickets your connected system returned.
            </p>
          )}
          {t.omitted.length > 0 && (
            <p className="hf-muted" style={{ fontSize: 'var(--t-12)', marginTop: 'var(--s-6)' }} role="note">
              Hidden by your permissions: {t.omitted.join(', ')}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
